import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { TvEngajamentoState } from '../../domain/entities/Engajamento';

/** Liquid glass — mesma família de sombras do NowPlayingToast / JukeboxQRCode */
const SHADOW_HUD =
  '0 8px 40px rgba(0,0,0,0.45), 0 0 28px rgba(251,146,60,0.06), inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(0,0,0,0.18)';

const ROTACAO_MS = 20_000;

interface Props {
  dados:           TvEngajamentoState;
  exibirFutebol:   boolean;
  pedidoCliente:   boolean;
  suprimir:        boolean;
}

interface ContadorItem {
  valor: number;
  rotulo: string;
  cor:   string;
}

function montarContadores(d: TvEngajamentoState, exibirFutebol: boolean): ContadorItem[] {
  const itens: ContadorItem[] = [];
  if (d.pedidos > 0) {
    itens.push({ valor: d.pedidos, rotulo: 'pedidos hoje', cor: 'text-orange-300/95' });
  }
  if (d.reacoes > 0) {
    itens.push({ valor: d.reacoes, rotulo: 'reações', cor: 'text-rose-300/95' });
  }
  if (exibirFutebol && d.votos > 0) {
    itens.push({ valor: d.votos, rotulo: 'votos no jogo', cor: 'text-emerald-300/95' });
  }
  return itens;
}

function LiquidGlassPanel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl px-4 py-3 border border-white/[0.12] backdrop-blur-[20px]"
      style={{
        background: 'linear-gradient(160deg, rgba(28,28,36,0.52) 0%, rgba(6,8,16,0.38) 100%)',
        boxShadow: SHADOW_HUD,
      }}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/[0.07] to-transparent"
        aria-hidden
      />
      <div className="relative">{children}</div>
    </div>
  );
}

export default function TvEngajamentoHud({
  dados,
  exibirFutebol,
  pedidoCliente,
  suprimir,
}: Props) {
  const [modoRanking, setModoRanking] = useState(false);

  const contadores = useMemo(
    () => montarContadores(dados, exibirFutebol),
    [dados, exibirFutebol],
  );
  const temRanking   = dados.ranking.length > 0;
  const temContadores = contadores.length > 0;

  useEffect(() => {
    if (suprimir || pedidoCliente || !temRanking || !temContadores) {
      setModoRanking(false);
      return;
    }
    const t = setInterval(() => {
      setModoRanking(prev => !prev);
    }, ROTACAO_MS);
    return () => clearInterval(t);
  }, [suprimir, pedidoCliente, temRanking, temContadores]);

  if (suprimir || (!temRanking && !temContadores)) return null;

  const mostrarRanking = temRanking && (modoRanking && !pedidoCliente || !temContadores);

  return (
    <div
      className="fixed z-10 pointer-events-none left-6 bottom-[clamp(88px,8vw,148px)] max-w-[min(92vw,420px)]"
      aria-live="polite"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={mostrarRanking ? 'ranking' : 'contadores'}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.35 }}
        >
          <LiquidGlassPanel>
            {mostrarRanking ? (
              <div>
                <p className="text-[clamp(9px,0.7vw,11px)] font-semibold uppercase tracking-widest text-orange-300/75 mb-2">
                  As mais pedidas
                </p>
                <ol className="space-y-1">
                  {dados.ranking.map((item, i) => (
                    <li
                      key={`${item.youtube_video_id}-${i}`}
                      className="flex items-baseline gap-2 text-[clamp(11px,0.95vw,15px)] leading-snug"
                    >
                      <span className="text-white/45 font-bold tabular-nums w-4 shrink-0">
                        {i + 1}.
                      </span>
                      <span className="text-white/90 font-medium line-clamp-1 flex-1 min-w-0">
                        {item.titulo}
                      </span>
                      <span className="text-white/50 text-[clamp(10px,0.8vw,13px)] tabular-nums shrink-0">
                        {item.pedidos}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : (
              <p className="text-[clamp(11px,0.95vw,15px)] text-white/85 font-medium tabular-nums flex flex-wrap items-center gap-x-2 gap-y-0.5">
                {contadores.map((item, i) => (
                  <span key={item.rotulo} className="inline-flex items-center gap-1">
                    {i > 0 && <span className="text-white/20" aria-hidden>·</span>}
                    <span className={item.cor}>{item.valor}</span>
                    <span>{item.rotulo}</span>
                  </span>
                ))}
              </p>
            )}
          </LiquidGlassPanel>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
